import { expenseGroupOf, visibleCategory, type ExpenseGroupKey } from './finance';
import type {
  ExpenseCategory,
  ExpenseEntry,
  Supplier,
  SupplierBilling,
} from './types';

export const EXPENSE_PAYMENT_METHOD_LABELS = {
  transferencia: 'Transferencia',
  tarjeta_debito: 'Tarjeta de débito',
  tarjeta_credito: 'Tarjeta de crédito',
  efectivo: 'Efectivo',
  otros: 'Otros',
} as const;

/**
 * Gastos (rediseño de Alex, 11 oct 2026; bloque G de la fase Finanzas,
 * 14 oct 2026). Reglas puras que comparten la API, la demo y la pantalla:
 *
 *  - Fijo o variable lo decide la CATEGORÍA (regla 7), no cada gasto.
 *  - Las notas y tickets sin factura cuentan el día de cada nota; al juntarlas
 *    con su factura, la factura las cierra y el gasto cuenta UNA vez, con la
 *    fecha de cada nota (decisión de Pol, 13 oct 2026).
 *  - «Esperando factura» sin alarma para quien factura una vez al mes (hasta
 *    10 días después de fin de mes) y nunca para quien no da factura.
 *  - Juntar notas: Alexia no adivina; solo suma y, si UNA sola combinación
 *    suma justo, la ofrece.
 */

const round2 = (n: number): number => Math.round(n * 100) / 100;

/* ------------------------------------------------------------------ */
/* Fijo o variable por categoría (regla 7)                             */
/* ------------------------------------------------------------------ */

// `NATURALEZA_POR_DEFECTO`, `naturalezaDe` y `conNaturalezaDeCategoria` viven
// en ./naturaleza (una sola fuente: `TenantSettings.expenseCategoryNature`).

/* ------------------------------------------------------------------ */
/* Totales de un gasto                                                 */
/* ------------------------------------------------------------------ */

/** Lo que pagas: el total de la factura (con IVA). */
export function totalDeGasto(
  e: Pick<ExpenseEntry, 'netAmount' | 'vatPct' | 'taxAmount' | 'invoiceTotal' | 'vatBreakdown'>,
): number {
  if (e.invoiceTotal != null) return round2(e.invoiceTotal);
  const iva =
    e.taxAmount ??
    e.vatBreakdown?.reduce((acc, f) => acc + f.tax, 0) ??
    (e.netAmount * (e.vatPct ?? 0)) / 100;
  return round2(e.netAmount + iva);
}

/** IVA en dinero de un gasto. */
export function ivaDeGasto(
  e: Pick<ExpenseEntry, 'netAmount' | 'vatPct' | 'taxAmount' | 'invoiceTotal' | 'vatBreakdown'>,
): number {
  return round2(totalDeGasto(e) - e.netAmount);
}

/**
 * Del total y el IVA en dinero (regla 1: «IVA EN DINERO, NO EN %») a lo que
 * se guarda: base = total − IVA, tipo efectivo con cuatro decimales y la
 * cuota y el total tal cual, para que cuadren al céntimo.
 */
export function importesDesdeTotal(
  total: number,
  iva: number,
): { netAmount: number; vatPct: number; taxAmount?: number; invoiceTotal: number } {
  const t = round2(total);
  const i = round2(Math.max(0, Math.min(iva, t)));
  const net = round2(t - i);
  const vatPct = i > 0 && net > 0 ? Math.round((i / net) * 100 * 10_000) / 10_000 : 0;
  return { netAmount: net, vatPct, ...(i > 0 ? { taxAmount: i } : {}), invoiceTotal: t };
}

/* ------------------------------------------------------------------ */
/* IVA del país y palabras                                             */
/* ------------------------------------------------------------------ */

/**
 * Tipo general de IVA de las compras por país (el «16 %» del atajo «¿Todo
 * lleva el mismo IVA?»). No es el de las ventas del restaurante: en España
 * se vende al 10 % y casi todo lo que se compra va al 21 %.
 */
const IVA_GENERAL: Record<string, number> = {
  MX: 16, ES: 21, AR: 21, CL: 19, CO: 19, PE: 18, UY: 22, EC: 15, GT: 12, CR: 13,
  DO: 18, PA: 7, BO: 13, PY: 10, SV: 13, HN: 15, NI: 15, VE: 16, PT: 23, IT: 22,
  FR: 20, DE: 19, AD: 4.5,
};

export function ivaGeneralDelPais(country: string | undefined, fallback = 16): number {
  return IVA_GENERAL[(country ?? '').trim().toUpperCase()] ?? fallback;
}

/**
 * Los tipos de IVA que se ven en las facturas de compra de cada país (los
 * botones de «IVA de la factura»). Donde no los tenemos, solo el general.
 * Casa Sergio (oct 2026) tecleaba «10» queriendo un 10 %: con un toque ya no
 * hace falta la cuenta.
 */
const TIPOS_IVA: Record<string, number[]> = {
  ES: [4, 10, 21],
  MX: [0, 16],
};

export function tiposDeIvaDelPais(country: string | undefined, fallback = 16): number[] {
  return TIPOS_IVA[(country ?? '').trim().toUpperCase()] ?? [ivaGeneralDelPais(country, fallback)];
}

/** El IVA en dinero de una factura de `total` (con IVA) a un tipo único: 110 al 10 % → 10. */
export function ivaConTipo(total: number, pct: number): number {
  if (!(total > 0) || !(pct > 0)) return 0;
  return round2(total - total / (1 + pct / 100));
}

/**
 * «10 %», «10%» o «10,5 %» tecleado en el IVA: es un porcentaje, no dinero.
 * Devuelve el tipo, o null si no lleva «%» (entonces es dinero).
 */
export function pctDeIvaTecleado(texto: string): number | null {
  const m = /^\s*(\d{1,2}(?:[.,]\d{1,2})?)\s*%\s*$/.exec(texto ?? '');
  if (!m) return null;
  const pct = Number(m[1]!.replace(',', '.'));
  return Number.isFinite(pct) ? pct : null;
}

/** El % que sale de un total y su IVA en dinero (sobre la base), con dos decimales; null sin IVA. */
export function tipoEfectivoDeIva(total: number, iva: number): number | null {
  const net = total - iva;
  if (!(iva > 0) || !(net > 0)) return null;
  return Math.round((iva / net) * 100 * 100) / 100;
}

/**
 * Cómo se llama la nota de entrega en cada país (textos que cambian:
 * «Albarán» solo en España). Para el resto, «nota».
 */
export function palabraNota(country: string | undefined): string {
  switch ((country ?? '').trim().toUpperCase()) {
    case 'MX': return 'remisión';
    case 'AR': return 'remito';
    case 'CL': return 'guía de despacho';
    case 'ES': return 'albarán';
    default: return 'nota';
  }
}

/** ¿El país escribe las fechas día/mes (03/04 = 3 de abril)? Casi todos sí. */
export function diaPrimeroEnPais(country: string | undefined): boolean {
  const c = (country ?? '').trim().toUpperCase();
  return !['US', 'PH', 'FM', 'MH', 'PW'].includes(c);
}

/* ------------------------------------------------------------------ */
/* Lo que recuerda el proveedor (regla 2)                              */
/* ------------------------------------------------------------------ */

export interface MemoriaProveedor {
  category?: ExpenseCategory;
  subgroup?: string;
  /** IVA / total: con un total de 116 y 0,1379… sale 16. */
  vatRatio?: number;
  creditDays?: number;
  billing?: SupplierBilling;
}

/**
 * Cómo se rellena un gasto nuevo de este proveedor: lo que guarda su ficha y,
 * si no lo tiene, su último gasto (los proveedores de antes no guardaban
 * nada). Así un gasto normal son tres campos: proveedor, total y fecha.
 */
export function memoriaDeProveedor(
  supplier: Pick<Supplier, 'creditDays' | 'vatRatio' | 'lastCategory' | 'lastSubgroup' | 'billing'> | undefined,
  ultimo?: Pick<ExpenseEntry, 'category' | 'subgroup' | 'netAmount' | 'vatPct' | 'taxAmount' | 'invoiceTotal' | 'vatBreakdown' | 'date' | 'dueDate' | 'paymentType' | 'documentType'>,
): MemoriaProveedor {
  const out: MemoriaProveedor = {};
  if (supplier?.billing) out.billing = supplier.billing;
  const categoria = supplier?.lastCategory ?? ultimo?.category;
  if (categoria) out.category = visibleCategory(categoria);
  const subgrupo = supplier?.lastCategory ? supplier.lastSubgroup : ultimo?.subgroup;
  if (subgrupo) out.subgroup = subgrupo;
  if (supplier?.vatRatio != null) out.vatRatio = supplier.vatRatio;
  else if (ultimo && (ultimo.documentType ?? 'factura') === 'factura') {
    const total = totalDeGasto(ultimo);
    out.vatRatio = total > 0 ? Math.round(((total - ultimo.netAmount) / total) * 1_000_000) / 1_000_000 : 0;
  }
  if (supplier?.creditDays != null) out.creditDays = supplier.creditDays;
  else if (ultimo?.paymentType === 'credito' && ultimo.dueDate) {
    out.creditDays = diasEntre(ultimo.date, ultimo.dueDate);
  }
  return out;
}

/** Días de crédito de un gasto que se debe (para recordarlos en el proveedor). */
export function diasEntre(desde: string, hasta: string): number {
  return Math.max(0, Math.round((Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000));
}

export function sumarDias(fecha: string, dias: number): string {
  return new Date(Date.parse(`${fecha}T00:00:00Z`) + dias * 86_400_000).toISOString().slice(0, 10);
}

/** IVA que propone el panel: el total por la proporción de la última factura. */
export function ivaPropuesto(total: number, vatRatio: number | undefined): number | undefined {
  if (vatRatio == null || !(total > 0)) return undefined;
  return round2(total * vatRatio);
}

/* ------------------------------------------------------------------ */
/* ¿Le falta la factura? (reglas 4 y 6)                                */
/* ------------------------------------------------------------------ */

/**
 * Categorías que nunca avisan de «Sin factura» (regla 6): una nómina no
 * tiene factura. «Propinas» no es una categoría de la app.
 */
export const CATEGORIAS_SIN_FACTURA: ReadonlySet<ExpenseCategory> = new Set<ExpenseCategory>([
  'sueldos_salarios',
  'cuotas_ss',
  'comisiones_bancarias',
]);

/** Días después de fin de mes en que una nota de «una factura al mes» empieza a avisar. */
export const DIAS_GRACIA_FACTURA_MENSUAL = 10;
/** Días que espera una nota de un proveedor que factura cada vez. */
export const DIAS_GRACIA_FACTURA = 10;

/**
 *  - `ok`: nada que decir (lo normal no se etiqueta).
 *  - `esperando`: «Esperando factura», sin alarma.
 *  - `sin_factura`: una factura ESPERADA que ya debería haber llegado (aviso ámbar).
 *    No equivale al invoiceStatus explícito «sin_factura», que no reclama nada.
 */
export type EstadoFactura = 'ok' | 'esperando' | 'sin_factura';

/** Último día del mes de una fecha. */
function finDeMes(fecha: string): string {
  const [y, m] = fecha.slice(0, 7).split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

export function estadoFactura(
  e: Pick<ExpenseEntry, 'documentType' | 'reconciledInvoiceId' | 'invoiceStatus' | 'category' | 'date'>,
  supplier: Pick<Supplier, 'billing'> | undefined,
  hoy: string,
): EstadoFactura {
  // «Sin factura» es una decisión explícita: no hay un documento que reclamar.
  if (e.invoiceStatus === 'sin_factura') return 'ok';
  if (CATEGORIAS_SIN_FACTURA.has(visibleCategory(e.category))) return 'ok';
  if (supplier?.billing === 'nunca') return 'ok';
  const esNota = e.documentType === 'albaran';
  if (esNota) {
    if (e.reconciledInvoiceId) return 'ok';
    const limite =
      supplier?.billing === 'mensual'
        ? sumarDias(finDeMes(e.date), DIAS_GRACIA_FACTURA_MENSUAL)
        : sumarDias(e.date, DIAS_GRACIA_FACTURA);
    return hoy > limite ? 'sin_factura' : 'esperando';
  }
  if (e.invoiceStatus === 'pendiente') return 'esperando';
  return 'ok';
}

/* ------------------------------------------------------------------ */
/* Lo que cuenta en el resultado (decisión de Pol, 13 oct 2026)         */
/* ------------------------------------------------------------------ */

/**
 * Al juntar notas con su factura: cuánto cuenta cada nota y cuánto la
 * factura. La factura aporta el IVA: cada nota cuenta su parte SIN IVA, en
 * su fecha; la factura solo la diferencia que las notas no cubren («Faltan
 * $505.70… la diferencia queda como un gasto más, con la fecha de la
 * factura»).
 *
 *  - Las notas suman lo de la factura sin IVA (iban sin IVA): cuentan tal cual.
 *  - Suman lo de la factura (o menos): se les quita el IVA de la factura y la
 *    factura cuenta lo que falta.
 *  - Se pasan (datos de antes; hoy no se deja juntar): se reparte la factura.
 */
export function repartoAlJuntar(
  factura: Pick<ExpenseEntry, 'netAmount' | 'vatPct' | 'taxAmount' | 'invoiceTotal' | 'vatBreakdown'>,
  notas: Array<Pick<ExpenseEntry, 'entryId' | 'netAmount' | 'vatPct' | 'taxAmount' | 'invoiceTotal' | 'vatBreakdown'>>,
): { factura: number; notas: Map<string, number> } {
  const neto = factura.netAmount;
  const total = totalDeGasto(factura);
  const suma = notas.reduce((acc, n) => acc + totalDeGasto(n), 0);
  const out = new Map<string, number>();
  if (suma <= 0) return { factura: round2(neto), notas: out };
  let f: number;
  let resto: number;
  if (Math.abs(suma - neto) <= TOLERANCIA_JUNTAR) {
    f = neto / suma;
    resto = 0;
  } else if (suma <= total + TOLERANCIA_JUNTAR) {
    f = total > 0 ? neto / total : 1;
    resto = Math.max(0, neto - suma * f);
  } else {
    f = neto / suma;
    resto = 0;
  }
  let repartido = 0;
  notas.forEach((n, i) => {
    const parte = i === notas.length - 1 && resto === 0
      ? round2(neto - repartido)
      : round2(totalDeGasto(n) * f);
    repartido = round2(repartido + parte);
    out.set(n.entryId, parte);
  });
  return { factura: resto === 0 ? 0 : round2(neto - repartido), notas: out };
}

/**
 * Los gastos tal y como cuentan en el resultado:
 *  - Fuera los que están «Por confirmar».
 *  - Una nota sin juntar cuenta su total, el día de la nota.
 *  - Una nota juntada cuenta su parte sin IVA (con el IVA de la factura, para
 *    que la caja siga saliendo con lo que se pagó).
 *  - Una factura con notas cuenta solo la diferencia (o nada).
 *
 * Datos de antes de este cambio (juntados sin `netoQueCuenta`): se calcula con
 * lo que haya cargado; si falta la otra parte, la nota cuenta su importe y la
 * factura la diferencia que se guardó al conciliar.
 */
export function gastosQueCuentan(expenses: ExpenseEntry[]): ExpenseEntry[] {
  const vivos = expenses.filter((e) => !e.porConfirmar);
  const porId = new Map(vivos.map((e) => [e.entryId, e]));
  const repartoCalculado = new Map<string, { factura: number; notas: Map<string, number> }>();
  const repartoDe = (factura: ExpenseEntry) => {
    let r = repartoCalculado.get(factura.entryId);
    if (!r) {
      const notas = (factura.deliveryNoteIds ?? []).map((id) => porId.get(id)).filter((n): n is ExpenseEntry => Boolean(n));
      if (notas.length !== (factura.deliveryNoteIds ?? []).length) return undefined;
      r = repartoAlJuntar(factura, notas);
      repartoCalculado.set(factura.entryId, r);
    }
    return r;
  };
  const sinDesglose = { taxAmount: undefined, invoiceTotal: undefined, vatBreakdown: undefined, taxMode: undefined };
  const out: ExpenseEntry[] = [];
  for (const e of vivos) {
    const esNota = e.documentType === 'albaran';
    if (esNota && e.reconciledInvoiceId) {
      const factura = porId.get(e.reconciledInvoiceId);
      const neto = e.netoQueCuenta ?? (factura ? repartoDe(factura)?.notas.get(e.entryId) : undefined) ?? e.netAmount;
      if (neto <= 0) continue;
      // El pago lo lleva la factura: la nota juntada sale de la caja cuando se
      // paga la factura (y con su IVA), no el día de la nota.
      const pago = factura
        ? { paid: Boolean(factura.paid), paymentDate: factura.paymentDate ?? (factura.paid ? factura.date : undefined) }
        : {};
      out.push({ ...e, ...sinDesglose, ...pago, netAmount: round2(neto), vatPct: factura?.vatPct ?? e.vatPct });
      continue;
    }
    if (!esNota && (e.deliveryNoteIds?.length ?? 0) > 0) {
      const neto = e.netoQueCuenta ?? repartoDe(e)?.factura ?? Math.max(0, e.reconciliationDifference ?? 0);
      if (neto <= 0.004) continue;
      out.push({ ...e, ...sinDesglose, netAmount: round2(neto) });
      continue;
    }
    out.push(e);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Juntar notas con su factura (regla 5)                               */
/* ------------------------------------------------------------------ */

/** «± $1»: lo que se da por «justo» al juntar. */
export const TOLERANCIA_JUNTAR = 1;

export type CuadreJuntar =
  | { estado: 'nada' }
  | { estado: 'justo'; diferencia: number }
  | { estado: 'sin_iva'; diferencia: number }
  | { estado: 'faltan'; diferencia: number }
  | { estado: 'te_pasas'; diferencia: number };

/**
 * Lo que dice el panel al marcar notas. «Te pasas» no deja juntar; «Faltan»
 * sí (la diferencia queda como un gasto más, con la fecha de la factura).
 */
export function cuadreAlJuntar(sumaMarcadas: number, totalFactura: number, netoFactura: number): CuadreJuntar {
  if (sumaMarcadas <= 0) return { estado: 'nada' };
  const d = round2(totalFactura - sumaMarcadas);
  if (Math.abs(d) <= TOLERANCIA_JUNTAR) return { estado: 'justo', diferencia: d };
  if (Math.abs(netoFactura - sumaMarcadas) <= TOLERANCIA_JUNTAR) return { estado: 'sin_iva', diferencia: round2(netoFactura - sumaMarcadas) };
  if (d > 0) return { estado: 'faltan', diferencia: d };
  return { estado: 'te_pasas', diferencia: -d };
}

/**
 * El atajo «suman justo»: solo si existe UNA sola combinación de notas que
 * suma lo de la factura (± $1). Es aritmética, no IA. Con varias o ninguna,
 * nada. Con muchas notas (más de 20) no se busca: no es seguro.
 */
export function combinacionUnica(
  notas: Array<{ entryId: string; total: number }>,
  objetivo: number,
  tolerancia = TOLERANCIA_JUNTAR,
): string[] | undefined {
  const n = notas.length;
  if (n === 0 || n > 20 || objetivo <= 0) return undefined;
  const cents = notas.map((x) => Math.round(x.total * 100));
  const meta = Math.round(objetivo * 100);
  const tol = Math.round(tolerancia * 100);
  let encontrada: number | undefined;
  let cuantas = 0;
  // Ordenadas de mayor a menor para podar antes.
  const orden = cents.map((c, i) => ({ c, i })).sort((a, b) => b.c - a.c);
  const restoDesde = new Array(n + 1).fill(0);
  for (let k = n - 1; k >= 0; k -= 1) restoDesde[k] = restoDesde[k + 1] + orden[k].c;
  const buscar = (k: number, suma: number, mascara: number): void => {
    if (cuantas > 1) return;
    if (suma > meta + tol) return;
    if (suma + restoDesde[k] < meta - tol) return;
    if (k === n) {
      if (Math.abs(suma - meta) <= tol && mascara !== 0) {
        cuantas += 1;
        encontrada = mascara;
      }
      return;
    }
    buscar(k + 1, suma + orden[k].c, mascara | (1 << orden[k].i));
    buscar(k + 1, suma, mascara);
  };
  buscar(0, 0, 0);
  if (cuantas !== 1 || encontrada === undefined) return undefined;
  return notas.filter((_, i) => (encontrada! & (1 << i)) !== 0).map((x) => x.entryId);
}

/* ------------------------------------------------------------------ */
/* Gastos que parecen inversiones (regla 13; Inversiones regla 4)       */
/* ------------------------------------------------------------------ */

// `pareceInversion` vive en ./inversiones (una sola lista de palabras).

/* ------------------------------------------------------------------ */
/* En qué se fue (regla 11)                                            */
/* ------------------------------------------------------------------ */

export type GrupoGasto = ExpenseGroupKey | 'financieros';

export function grupoDeGasto(category: ExpenseCategory): GrupoGasto {
  return expenseGroupOf(visibleCategory(category)) ?? 'financieros';
}

/** Sin IVA por grupo de lo que cuenta en el resultado. */
export function enQueSeFue(expenses: ExpenseEntry[]): Record<GrupoGasto, number> {
  const out: Record<GrupoGasto, number> = { materia_prima: 0, personal: 0, generales: 0, financieros: 0 };
  for (const e of gastosQueCuentan(expenses)) {
    // Un gasto común ya llega con su parte (`allocateSharedExpenses`).
    out[grupoDeGasto(e.category)] += e.netAmount;
  }
  for (const k of Object.keys(out) as GrupoGasto[]) out[k] = round2(out[k]);
  return out;
}
